// Google Maps service for preloading and managing the Google Maps API
declare global {
  interface Window {
    google: any;
    googleMapsLoaded: boolean;
    googleMapsLoading: boolean;
  }
}

class MapService {
  private static instance: MapService;
  private loadPromise: Promise<void> | null = null;

  private constructor() {}

  public static getInstance(): MapService {
    if (!MapService.instance) {
      MapService.instance = new MapService();
    }
    return MapService.instance;
  }

  /**
   * Preload Google Maps API asynchronously
   * This should be called after successful login to prime the map for faster loading
   */
  public preloadGoogleMaps(): Promise<void> {
    // If already loaded, return immediately
    if (window.google && window.googleMapsLoaded) {
      console.log('Google Maps already loaded and ready');
      return Promise.resolve();
    }

    // If already loading, return the existing promise
    if (this.loadPromise) {
      console.log('Google Maps already loading, returning existing promise');
      return this.loadPromise;
    }

    // Check if script already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      console.log('Google Maps script already exists, waiting for it to load...');
      this.loadPromise = new Promise((resolve) => {
        const checkLoaded = () => {
          if (window.google && window.googleMapsLoaded) {
            console.log('Existing Google Maps script loaded');
            resolve();
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
      });
      return this.loadPromise;
    }

    // Create new script and load
    this.loadPromise = new Promise((resolve, reject) => {
      console.log('Preloading Google Maps API...');
      const script = document.createElement('script');
      const apiKey = 'AIzaSyBShSyYr2n-mrs2b1J23rt-93s3FNgVlOs';
      
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('Google Maps API preloaded successfully');
        window.googleMapsLoaded = true;
        resolve();
      };
      
      script.onerror = () => {
        console.error('Failed to preload Google Maps API');
        this.loadPromise = null; // Reset so we can try again
        reject(new Error('Failed to load Google Maps API'));
      };
      
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * Check if Google Maps is ready to use
   */
  public isGoogleMapsReady(): boolean {
    return !!(window.google && window.google.maps && window.googleMapsLoaded);
  }

  /**
   * Wait for Google Maps to be ready (if already loading)
   */
  public waitForGoogleMaps(): Promise<void> {
    if (this.isGoogleMapsReady()) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    // If not loading, start loading
    return this.preloadGoogleMaps();
  }

  /**
   * Reset the loading state (useful for testing or error recovery)
   */
  public reset(): void {
    this.loadPromise = null;
    window.googleMapsLoaded = false;
    window.googleMapsLoading = false;
  }
}

export const mapService = MapService.getInstance();
